"use client"

import { Box, HStack, Icon, Image, Text } from "@chakra-ui/react"
import { AnimatePresence, domAnimation, LazyMotion, m } from "motion/react"
import React, { useCallback, useEffect, useRef, useState } from "react"
import { EllipsisVerticalIcon } from "@/components/icons"

interface CompareProps {
  firstImage?: string | null
  secondImage?: string | null
  className?: string
  firstImageClassName?: string
  secondImageClassname?: string
  initialSliderPercentage?: number
  slideMode?: "hover" | "drag"
  showHandlebar?: boolean
  autoplay?: boolean
  autoplayDuration?: number
  roundedCorners?: string
  firstImageLabel?: string
  secondImageLabel?: string
}

const Compare = ({
  autoplay = false,
  autoplayDuration = 5000,
  className = "compare-container",
  firstImage,
  firstImageClassName,
  firstImageLabel,
  initialSliderPercentage = 50,
  roundedCorners = "none",
  secondImage,
  secondImageClassname,
  secondImageLabel,
  showHandlebar = true,
  slideMode = "hover",
}: CompareProps) => {
  const [sliderXPercentOverride, setSliderXPercentOverride] = useState<
    number | null
  >(null)
  const sliderXPercent = sliderXPercentOverride ?? initialSliderPercentage

  const sliderRef = useRef<HTMLDivElement>(null)
  const isDraggingRef = useRef(false)

  const autoplayRef = useRef<NodeJS.Timeout | null>(null)

  const startAutoplay = useCallback(() => {
    if (!autoplay) return

    const startTime = Date.now()
    const animate = () => {
      const elapsedTime = Date.now() - startTime
      const progress = (elapsedTime % (autoplayDuration * 2)) / autoplayDuration
      const percentage = progress <= 1 ? progress * 100 : (2 - progress) * 100

      setSliderXPercentOverride(percentage)
      autoplayRef.current = setTimeout(animate, 16) // ~60fps
    }

    animate()
  }, [autoplay, autoplayDuration])

  const stopAutoplay = useCallback(() => {
    if (autoplayRef.current) {
      clearTimeout(autoplayRef.current)
      autoplayRef.current = null
    }
  }, [])

  useEffect(() => {
    startAutoplay()
    return () => stopAutoplay()
  }, [startAutoplay, stopAutoplay])

  function mouseEnterHandler() {
    stopAutoplay()
  }

  function mouseLeaveHandler() {
    if (slideMode === "hover") {
      setSliderXPercentOverride(null)
    }
    if (slideMode === "drag") {
      isDraggingRef.current = false
    }
    startAutoplay()
  }

  const handleStart = useCallback(() => {
    if (slideMode === "drag") {
      isDraggingRef.current = true
    }
  }, [slideMode])

  const handleEnd = useCallback(() => {
    if (slideMode === "drag") {
      isDraggingRef.current = false
    }
  }, [slideMode])

  const handleMove = useCallback(
    (clientX: number) => {
      if (!sliderRef.current) return
      if (
        slideMode === "hover" ||
        (slideMode === "drag" && isDraggingRef.current)
      ) {
        const rect = sliderRef.current.getBoundingClientRect()
        const x = clientX - rect.left
        const percent = (x / rect.width) * 100
        requestAnimationFrame(() => {
          setSliderXPercentOverride(Math.max(0, Math.min(100, percent)))
        })
      }
    },
    [slideMode]
  )

  const handleMouseDown = useCallback(() => handleStart(), [handleStart])
  const handleMouseUp = useCallback(() => handleEnd(), [handleEnd])
  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => handleMove(e.clientX),
    [handleMove]
  )

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (!autoplay && e.touches?.[0]) {
        handleStart()
      }
    },
    [handleStart, autoplay]
  )

  const handleTouchEnd = useCallback(() => {
    if (!autoplay) {
      handleEnd()
    }
  }, [handleEnd, autoplay])

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (!autoplay && e.touches?.[0]) {
        handleMove(e.touches[0].clientX)
      }
    },
    [handleMove, autoplay]
  )

  return (
    <LazyMotion features={domAnimation}>
      <Box
        className={className}
        cursor={slideMode === "drag" ? "grab" : "col-resize"}
        data-print-compare=""
        h={{ base: 210, sm: 320, md: 440, lg: "600px" }}
        onMouseDown={handleMouseDown}
        onMouseEnter={mouseEnterHandler}
        onMouseLeave={mouseLeaveHandler}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onTouchEnd={handleTouchEnd}
        onTouchMove={handleTouchMove}
        onTouchStart={handleTouchStart}
        overflow="hidden"
        position="relative"
        ref={sliderRef}
        w="100%"
      >
        <AnimatePresence initial={false}>
          <Box
            asChild
            css={{
              backgroundImage:
                "linear-gradient(to bottom,transparent, var(--gil-colors-primary-400), transparent)",
            }}
            cursor={slideMode === "drag" ? "grab" : "col-resize"}
            data-print-compare-ui=""
            h="full"
            m="auto"
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
            onTouchEnd={handleTouchEnd}
            onTouchStart={handleTouchStart}
            position="absolute"
            top="0"
            w="1px"
            zIndex={40}
          >
            <m.div
              style={{
                left: `${sliderXPercent}%`,
              }}
              transition={{ duration: 0 }}
            >
              {/* Left gradient overlay */}
              <Box
                className="mask-radial-gradient-1"
                css={{
                  backgroundImage:
                    "linear-gradient(to right, var(--gil-colors-primary-400) 0px, rgba(0, 0, 0, 0) 50%, rgba(0, 0, 0, 0) 100%)",
                  maskImage: "radial-gradient(100px at 0,#fff,#0000)",
                }}
                h="full"
                left="0"
                opacity={0.6}
                pointerEvents="none"
                position="absolute"
                top="0"
                w="144px"
                zIndex={20}
              />
              {/* Center gradient overlay */}
              <Box
                className="mask-radial-gradient-2"
                css={{
                  backgroundImage:
                    "linear-gradient(to right, var(--gil-colors-primary-400) 0px, rgba(0, 0, 0, 0) 50%, rgba(0, 0, 0, 0) 100%)",
                  maskImage:
                    "radial-gradient(50px at 0px center, rgb(255, 255, 255), rgba(0, 0, 0, 0))",
                }}
                h="50%"
                left="0"
                opacity={0.8}
                pointerEvents="none"
                position="absolute"
                top="25%"
                w="40px"
                zIndex={15}
              />
              <Box
                css={{
                  maskImage:
                    "radial-gradient(40px at left, white, transparent)",
                }}
                h="75%"
                pointerEvents="none"
                position="absolute"
                right="-40px"
                top="12.5%"
                w="40px"
              ></Box>
              <HStack
                bgColor="bg.paper"
                borderColor="border"
                borderRadius="md"
                borderStyle="solid"
                borderWidth={"1px"}
                bottom="12px"
                data-print-compare-ui=""
                flexDirection="row-reverse"
                position="absolute"
                px={4}
                py={2}
                right="12px"
              >
                <Text color="fg.muted" fontSize="sm">
                  {firstImageLabel || "Before"}
                </Text>
              </HStack>
              <HStack
                bgColor="bg.paper"
                borderColor="border"
                borderRadius="md"
                borderStyle="solid"
                borderWidth={"1px"}
                bottom="12px"
                data-print-compare-ui=""
                left="12px"
                position="absolute"
                px={4}
                py={2}
              >
                <Text color="fg.muted" fontSize="sm">
                  {secondImageLabel || "After"}
                </Text>
              </HStack>
              {showHandlebar && (
                <Box
                  _hover={{
                    bg: "gray.50",
                    transform: "translateY(-50%) scale(1.1)",
                  }}
                  alignItems="center"
                  bg="white"
                  boxShadow="0px -1px 0px 0px rgba(255, 255, 255, 0.25)"
                  cursor={slideMode === "drag" ? "grab" : "col-resize"}
                  display="flex"
                  h="20px"
                  justifyContent="center"
                  position="absolute"
                  right="-10px"
                  rounded="md"
                  top="50%"
                  transform="translateY(-50%)"
                  transition="all 0.2s"
                  userSelect="none"
                  w="20px"
                  zIndex={30}
                >
                  <Icon boxSize="16px" color="black">
                    <EllipsisVerticalIcon />
                  </Icon>
                </Box>
              )}
            </m.div>
          </Box>
        </AnimatePresence>
        {/* Second image (background) */}
        <AnimatePresence initial={false}>
          {secondImage ? (
            <Box
              cursor={slideMode === "drag" ? "grab" : "col-resize"}
              data-print-compare-image=""
              h="full"
              left="0"
              overflow="hidden"
              position="absolute"
              rounded={roundedCorners}
              top="0"
              w="full"
              zIndex={10}
            >
              <Image
                alt="second image"
                className={secondImageClassname}
                draggable={false}
                h="full"
                objectFit="contain"
                objectPosition="center"
                src={secondImage}
                userSelect="none"
                w="full"
              />
            </Box>
          ) : null}
        </AnimatePresence>

        {/* First image (foreground with clip) */}
        <AnimatePresence initial={false}>
          {firstImage ? (
            <Box
              cursor={slideMode === "drag" ? "grab" : "col-resize"}
              data-print-compare-image=""
              h="full"
              left="0"
              overflow="hidden"
              position="absolute"
              rounded={roundedCorners}
              style={{
                clipPath: `inset(0 ${100 - sliderXPercent}% 0 0)`,
              }}
              top="0"
              w="full"
              zIndex={20}
            >
              <Image
                alt="first image"
                className={firstImageClassName}
                draggable={false}
                h="full"
                objectFit="contain"
                objectPosition="center"
                src={firstImage}
                w="full"
              />
            </Box>
          ) : null}
        </AnimatePresence>
      </Box>
    </LazyMotion>
  )
}

export default Compare
